import { Trans, useLingui } from '@lingui/react/macro';
import { AlertCircle, FolderPlus, LayoutTemplate, Plus, SearchX } from 'lucide-react';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import { useMediaManagerTemplateGroupsQuery } from 'modules/manager/store/media-manager-api';
import MediaManagerTemplateGroupCard from 'modules/manager/ui/components/media-manager-template-group-card';
import { GROUP_GRID_CLASS } from 'modules/manager/ui/components/preset-card-metrics';
import { TemplateGroupGridSkeleton } from 'modules/manager/ui/components/skeletons';
import useCreateTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-create-template-group-modal';
import useDeleteTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-delete-template-group-modal';
import useRenameTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-rename-template-group-modal';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deriveAsyncState } from 'shared/utils/async-state';
import classNames from 'classnames';
import UDButton from 'ud-ui/components/button';
import UDHeaderTitle from 'ud-ui/components/header-title';
import UDEmptyState from 'ud-ui/components/empty-state';
import { UDPagedListToolbar } from 'ud-ui/components/paged-list';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';

const MediaManagerTemplatesListPage: React.FC = () => {
  const { t } = useLingui();
  const { setHeaderContent } = useMainLayout();
  const navigate = useNavigate();
  const tournamentId = useSelector(authStoreUserTournamentId);
  const [search, setSearch] = useState('');

  const {
    data: groups = [],
    currentData,
    isFetching,
    isError,
    refetch,
  } = useMediaManagerTemplateGroupsQuery({ tournamentId: tournamentId ?? 0 }, { skip: !tournamentId });
  const listState = deriveAsyncState({
    itemsCount: groups.length,
    hasCurrentData: currentData !== undefined,
    isFetching,
    isError,
  });

  useEffect(() => {
    setHeaderContent(
      <UDHeaderTitle>
        <Trans>Медиа менеджер</Trans>
      </UDHeaderTitle>,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const loadGroups = useCallback(() => {
    void refetch();
  }, [refetch]);

  const visibleGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? groups.filter((group) => group.name.toLowerCase().includes(query)) : groups;
  }, [groups, search]);

  const existingNames = useMemo(() => groups.map((group) => group.name.trim()), [groups]);
  // The list refetches by the mutations' tags — the modals take no «reload» callback (IMGM-1515).
  const { modal: createModal, open: openCreateModal } = useCreateTemplateGroupModal({
    tournamentId: tournamentId ?? null,
    existingNames,
  });
  const { modal: renameModal, open: openRenameModal } = useRenameTemplateGroupModal({ groups });
  const { modal: deleteModal, open: openDeleteModal } = useDeleteTemplateGroupModal();

  const openGroup = useCallback(
    (group: MediaManagerTemplateGroup) => {
      navigate(Pages.MEDIA_MANAGER.builders.group(group.id));
    },
    [navigate],
  );

  const actions = (
    <>
      <UDButton
        variant="secondary"
        className="bg-surfaceCard flex items-center"
        onClick={() => navigate(Pages.MEDIA_MANAGER.TEMPLATES_LIBRARY)}
      >
        <LayoutTemplate size={16} className="mr-2" />
        <Trans>Общие шаблоны</Trans>
      </UDButton>
      <UDButton variant="primary" className="flex items-center" onClick={openCreateModal}>
        <Plus size={16} className="mr-2" />
        <Trans>Создать группу</Trans>
      </UDButton>
    </>
  );

  return (
    <div className="flex h-full flex-col p-6">
      {/* Поиск по группам нужен, только когда есть что искать; иначе остаются одни кнопки справа. */}
      {groups.length > 0 ? (
        <UDPagedListToolbar search={search} onSearchChange={setSearch} searchPlaceholder={t`Поиск`} actions={actions} />
      ) : (
        <div className="mb-4 flex justify-end gap-3">{actions}</div>
      )}

      {listState.status === 'loading' ? (
        <TemplateGroupGridSkeleton canWrite />
      ) : listState.status === 'error' ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4" role="alert">
          <AlertCircle size={40} className="text-inkMuted" aria-hidden />
          <UDEmptyState
            title={<Trans>Не удалось загрузить группы шаблонов</Trans>}
            action={
              <UDButton variant="secondary" onClick={loadGroups}>
                <Trans>Повторить</Trans>
              </UDButton>
            }
          />
        </div>
      ) : listState.status === 'empty' ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <div className="bg-surfaceSunken rounded-pill flex size-20 items-center justify-center">
            <FolderPlus size={36} className="text-inkMuted" aria-hidden />
          </div>
          <UDEmptyState
            title={<Trans>Пока нет групп шаблонов</Trans>}
            action={
              <UDButton variant="primary" className="flex items-center" onClick={openCreateModal}>
                <Plus size={16} className="mr-2" />
                <Trans>Создать группу</Trans>
              </UDButton>
            }
          />
        </div>
      ) : visibleGroups.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <SearchX size={40} className="text-inkMuted" aria-hidden />
          <UDEmptyState title={<Trans>Ничего не найдено</Trans>} />
        </div>
      ) : (
        <div
          className={classNames(GROUP_GRID_CLASS, 'transition-opacity', {
            'pointer-events-none opacity-50': listState.isRefreshing,
          })}
          aria-busy={listState.isRefreshing}
        >
          {visibleGroups.map((group) => (
            <MediaManagerTemplateGroupCard
              key={group.id}
              group={group}
              canWrite
              onOpen={openGroup}
              onRename={openRenameModal}
              onDelete={openDeleteModal}
            />
          ))}
        </div>
      )}

      {createModal}
      {renameModal}
      {deleteModal}
    </div>
  );
};

export default MediaManagerTemplatesListPage;
